import { useState } from "react";
import { motion } from "framer-motion";
import {
  CHARACTERS_MAP,
  MUSIC_MAP,
  CRAFTS_MAP,
  QUOTES_MAP,
  TRIVIA_MAP,
  LOCATIONS_MAP,
  SILHOUETTES_MAP,
} from "../maps";
import { CHARACTERS, QUOTES, MUSIC, LOCATIONS, TRIVIA, CRAFTS, SILHOUETTES } from "../data";
import { getDateString, getTodayString, getDailyItem } from "../utils/gameLogic";

/**
 * Calendar-style date picker for replaying past daily puzzles.
 * Each cell shows colored dots indicating win/loss/partial status per mode.
 * Dates before the epoch (2026-04-01) and future dates are disabled.
 *
 * @param {{ onSelectDate: (date: Date) => void, currentTheme: string }} props
 * @param {(date: Date) => void} props.onSelectDate - Callback when a past date is clicked.
 * @param {string} props.currentTheme - Active era theme name.
 * @returns {JSX.Element} The archive calendar view.
 */
function ArchiveMode({ onSelectDate, currentTheme }) {
  const today = new Date();
  const todayYear = today.getUTCFullYear();
  const todayMonth = today.getUTCMonth();
  const todayDate = today.getUTCDate();

  const [viewYear, setViewYear] = useState(todayYear);
  const [viewMonth, setViewMonth] = useState(todayMonth);
  const epochDate = new Date("2026-04-01T00:00:00Z");

  /** @type {Array<{key: string, data: Array, map: Array<number>, max: number}>} */
  const PUZZLES = [
    { key: "kisekidle-chars", data: CHARACTERS, map: CHARACTERS_MAP, max: 10 },
    { key: "kisekidle-quotes", data: QUOTES, map: QUOTES_MAP, max: 6 },
    { key: "kisekidle-music", data: MUSIC, map: MUSIC_MAP, max: 6 },
    { key: "kisekidle-locations", data: LOCATIONS, map: LOCATIONS_MAP, max: 5 },
    { key: "kisekidle-trivia", data: TRIVIA, map: TRIVIA_MAP, max: 2 },
    { key: "kisekidle-crafts", data: CRAFTS, map: CRAFTS_MAP, max: 6 },
    {
      key: "kisekidle-silhouette",
      data: SILHOUETTES,
      map: SILHOUETTES_MAP,
      max: 5,
    },
  ];

  /** Checks localStorage for all 7 modes on a given date and returns per-mode status strings. */
  const getDayStatuses = (dateObj) => {
    return PUZZLES.map((puzzle) => {
      const targetDateStr = getDateString(dateObj);

      // ALWAYS use the date-specific key
      const storageKey = `${puzzle.key}-${targetDateStr}`;
      let storedData = localStorage.getItem(storageKey);

      // MIGRATION FALLBACK
      const currentTodayStr = getTodayString();
      if (!storedData && targetDateStr === currentTodayStr) {
        const oldData = localStorage.getItem(puzzle.key);
        if (oldData) {
          const parsedOld = JSON.parse(oldData);
          if (parsedOld.date === targetDateStr) storedData = oldData;
        }
      }

      if (!storedData) return "unplayed";

      try {
        const parsed = JSON.parse(storedData);
        const guesses = parsed.guesses || [];
        if (guesses.length === 0) return "unplayed";

        const targetItem = getDailyItem(puzzle.data, dateObj, puzzle.map);

        const isWin = guesses.some((guess) => {
          if (typeof guess === "string") {
            if (puzzle.key === "kisekidle-trivia") {
              return String(guess).toLowerCase() === String(targetItem.answer).toLowerCase();
            }
            if (puzzle.key === "kisekidle-silhouette") {
              return String(guess).toLowerCase() === String(targetItem.character).toLowerCase();
            }
            return false;
          }

          if (guess && typeof guess === "object") {
            const gId = guess.id != null ? String(guess.id) : null;
            const gName = guess.name ? String(guess.name).toLowerCase() : null;
            const gCraft = guess.craftName ? String(guess.craftName).toLowerCase() : null;

            if (puzzle.key === "kisekidle-chars" || puzzle.key === "kisekidle-music" || puzzle.key === "kisekidle-locations") {
              return gId !== null && gId === String(targetItem.id);
            }
            if (puzzle.key === "kisekidle-quotes") {
              return gName !== null && gName === String(targetItem.character).toLowerCase();
            }
            if (puzzle.key === "kisekidle-crafts") {
              const tName = String(targetItem.character).toLowerCase();
              const tCraft = String(targetItem.craftName).toLowerCase();
              const gChar = guess.character ? String(guess.character).toLowerCase() : null;
              if (gChar !== null && gChar === tName) return true;
              return (gName !== null && gName === tName) || (gCraft !== null && gCraft === tCraft);
            }
          }
          return false;
        });

        if (isWin) return "win";
        if (guesses.length >= puzzle.max) return "loss";
        return "partial";
      } catch (e) {
        return "unplayed";
      }
    });
  };

  /** Navigates the calendar to the previous month. */
  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((prev) => prev - 1);
    } else {
      setViewMonth((prev) => prev - 1);
    }
  };

  /** Navigates the calendar to the next month (clamped to today). */
  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((prev) => prev + 1);
    } else {
      setViewMonth((prev) => prev + 1);
    }
  };

  const daysInMonth = new Date(
    Date.UTC(viewYear, viewMonth + 1, 0)
  ).getUTCDate();
  const firstDayOfWeek = new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay();

  const blanks = Array.from({ length: firstDayOfWeek }, (_, i) => i);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className={`archive-container theme-${currentTheme}`}>
      <div className="header-container">
        <h2 className="mode-title">📅 Daily Archive</h2>
        <p style={{ color: "#a0a5b5", marginBottom: "20px" }}>
          Select a past date to replay missed puzzles.
        </p>
      </div>

      <div className="calendar-wrapper">
        <div className="calendar-header">
          <button
            className="calendar-nav-btn"
            onClick={handlePrevMonth}
            disabled={viewYear === 2024 && viewMonth === 0}
          >
            ◀
          </button>
          <h3>
            {monthNames[viewMonth]} {viewYear}
          </h3>
          <button
            className="calendar-nav-btn"
            onClick={handleNextMonth}
            disabled={viewYear === todayYear && viewMonth === todayMonth}
          >
            ▶
          </button>
        </div>

        <motion.div 
          className="calendar-grid"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.01 } }
          }}
        >
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="calendar-day-label">
              {day}
            </div>
          ))}
          {blanks.map((blank) => (
            <div key={`blank-${blank}`} className="calendar-empty-slot"></div>
          ))}

          {days.map((day) => {
            const currentCellDate = new Date(
              Date.UTC(viewYear, viewMonth, day)
            );
            const isBeforeEpoch = currentCellDate < epochDate;
            const isFuture =
              viewYear > todayYear ||
              (viewYear === todayYear && viewMonth > todayMonth) ||
              (viewYear === todayYear &&
                viewMonth === todayMonth &&
                day > todayDate);
            const isTodayCell =
              viewYear === todayYear &&
              viewMonth === todayMonth &&
              day === todayDate;
            const isDisabled = isBeforeEpoch || isFuture || isTodayCell;

            const statuses =
              isBeforeEpoch || isFuture ? [] : getDayStatuses(currentCellDate);
            const hasAnyPlays = statuses.some((s) => s !== "unplayed");

            return (
              <motion.button
                key={day}
                variants={{
                  hidden: { opacity: 0, scale: 0.8 },
                  visible: { opacity: 1, scale: 1 }
                }}
                className={`calendar-day-btn ${isTodayCell ? "calendar-today" : ""
                  }`}
                disabled={isDisabled}
                onClick={() => onSelectDate(currentCellDate)}
                title={
                  isTodayCell ? "Play Today's Puzzle on the main tab!" : ""
                }
              >
                <span className="calendar-day-text">{day}</span>
                {hasAnyPlays && (
                  <div className="calendar-dots-grid">
                    {statuses.map((status, idx) => (
                      <span
                        key={idx}
                        className={`calendar-dot dot-${status}`}
                        title={`Mode: ${PUZZLES[idx].key}`}
                      ></span>
                    ))}
                  </div>
                )}
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}

export default ArchiveMode;
